/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC } from 'react'
import { Routes, Route, BrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { PrivateRoutes } from './PrivateRoutes'
import { ErrorsPage } from '../modules/errors/ErrorsPage'
import { Logout, AuthPage, useAuth } from '../modules/auth'
import { App } from '../App'
import { TeleConsultaPage } from '../modules/tele-consulta'
import { AuthLayout } from '../../_metronic/layout/AuthLayout'
import { AutenticacaoPage } from '../modules/apps/atendimento-ASO/Autenticacao/AutenticacaoPage'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import { UploadPage } from '../modules/public/upload/UploadPage'
import { ResetPassword } from '../modules/auth/components/ResetPassword'
import { TermoCiencia } from '../modules/auth/components/TermoCiencia'
import { ConfirmAccount } from '../modules/auth/components/ConfirmAccount'
import { CertificadoPage } from '../modules/public/certificado/CertificadoPage'
import { AssinaturaContent } from '../modules/public/assinatura/AssinaturaContent'
import { PrescricaoContent } from '../modules/public/prescricao/components/content/PrescricaoContent'
import { DispensacaoContent } from '../modules/public/dispensacao/DispensacaoContent'
import { DownloadPage } from '../modules/public/download/DownloadPage'
import { getLoginRedirectPath } from '../modules/auth/core/AuthHelpers'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env

const AppRoutes: FC = () => {
  const { currentUser } = useAuth()

  const RedirectToAutenticar = () => {
    const location = useLocation()
    return <Navigate to={`/autenticar${location.search}`} replace />
  }

  return (
    // Comportamentos da v7 adotados já: sem eles o 6.30 avisa no console a cada carga.
    <BrowserRouter
      basename={PUBLIC_URL}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {currentUser ? (
            currentUser.termoCienciaPendente ? (
              // LGPD: com o termo de ciência pendente o backend recusa tudo (403), então o app
              // nem é montado — só a tela do termo, abaixo, fica alcançável.
              <Route path='*' element={<Navigate to='/termo-ciencia' replace />} />
            ) : (
              <Route element={<MasterLayout />}>
                <Route path='/*' element={<PrivateRoutes />} />
              </Route>
            )
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to={getLoginRedirectPath()} replace />} />
            </>
          )}
          <Route path='tele-consulta/*' element={<TeleConsultaPage />} />
          <Route path='termo-ciencia' element={<TermoCiencia />} />
          <Route element={<AuthLayout />}>
            <Route path='authenticate/*' element={<RedirectToAutenticar />} />
            <Route path='autenticar/*' element={<AutenticacaoPage />} />

            <Route path='prescricao/:tokenPrescricao' element={<PrescricaoContent />} />
            <Route path='prescricao' element={<PrescricaoContent />} />
            <Route path='dispensacao/:tokenPrescricao' element={<DispensacaoContent />} />
            <Route path='dispensacao' element={<DispensacaoContent />} />
            <Route path='assinatura/:token' element={<AssinaturaContent />} />
            <Route path='download/:tipo' element={<DownloadPage />} />
            <Route path='download/:tipo/:protocolo' element={<DownloadPage />} />
            <Route path='upload/:tokenUpload' element={<UploadPage />} />
            <Route path='reset-password/*' element={<ResetPassword />} />
            <Route path='confirm-account/*' element={<ConfirmAccount />} />
            <Route path='integraicp/credencial/:token' element={<CertificadoPage />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export { AppRoutes }
